import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import apiService, { api, orderService, calculatorService } from '../services/api';
import { formatCurrency, formatDate } from '../utils/formatters';
import CalculatorEditModal from '../components/Calculator/CalculatorEditModal';
import ClientSelectWithCreate from '../components/Common/ClientSelectWithCreate';

const DetailContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 1rem;

  @media (min-width: 768px) {
    padding: 0 2rem;
  }
`;

const BackButton = styled.button`
  padding: 0.4rem 0.75rem;
  background-color: #6c757d;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  margin-bottom: 1rem;
  font-size: 0.9rem;

  &:hover {
    background-color: #5a6268;
  }

  @media (min-width: 768px) {
    padding: 0.5rem 1rem;
    margin-bottom: 2rem;
    font-size: 1rem;
  }
`;

const OrderCard = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  padding: 1rem;

  @media (min-width: 768px) {
    padding: 2rem;
  }
`;

const OrderHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 2px solid #e0e0e0;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: start;
    margin-bottom: 2rem;
  }
`;

const OrderInfo = styled.div``;

const OrderNumber = styled.h1`
  color: #333;
  margin: 0 0 0.25rem 0;
  font-size: 1.25rem;

  @media (min-width: 768px) {
    font-size: 1.75rem;
    margin: 0 0 0.5rem 0;
  }
`;

const OrderDate = styled.p`
  color: #666;
  margin: 0;
  font-size: 0.85rem;

  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const Section = styled.div`
  margin-bottom: 1.5rem;

  @media (min-width: 768px) {
    margin-bottom: 2rem;
  }
`;

const SectionTitle = styled.h2`
  color: #555;
  font-size: 1rem;
  margin-bottom: 0.75rem;

  @media (min-width: 768px) {
    font-size: 1.2rem;
    margin-bottom: 1rem;
  }
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.5rem;

  @media (min-width: 500px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
`;

const InfoItem = styled.div`
  padding: 0.35rem 0;

  @media (min-width: 768px) {
    padding: 0.5rem 0;
  }
`;

const InfoLabel = styled.span`
  color: #666;
  font-weight: 500;
  margin-right: 0.5rem;
  font-size: 0.85rem;
  display: block;

  @media (min-width: 500px) {
    display: inline;
    font-size: 1rem;
  }
`;

const InfoValue = styled.span`
  color: #333;
  font-size: 0.9rem;
  word-break: break-word;

  @media (min-width: 768px) {
    font-size: 1rem;
  }
`;

const ItemsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-top: 1rem;
  display: none;

  @media (min-width: 768px) {
    display: table;
  }
`;

const TableHeader = styled.th`
  background-color: #f8f9fa;
  padding: 0.5rem;
  text-align: left;
  font-weight: 600;
  color: #555;
  border-bottom: 2px solid #dee2e6;
  font-size: 0.85rem;

  @media (min-width: 768px) {
    padding: 0.75rem;
    font-size: 1rem;
  }
`;

const TableCell = styled.td`
  padding: 0.5rem;
  border-bottom: 1px solid #dee2e6;
  font-size: 0.85rem;

  @media (min-width: 768px) {
    padding: 0.75rem;
    font-size: 1rem;
  }
`;

// Mobile cards for articles
const MobileArticlesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1rem;

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileArticleCard = styled.div`
  background: #f8f9fa;
  border-radius: 8px;
  padding: 1rem;
  border: 1px solid #e0e0e0;
`;

const MobileArticleDescription = styled.div`
  font-size: 0.9rem;
  color: #333;
  margin-bottom: 0.75rem;
  line-height: 1.4;
  word-break: break-word;
`;

const MobileArticleDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
`;

const MobileArticleInfo = styled.span`
  font-size: 0.85rem;
  color: #666;
`;

const MobileArticleTotal = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #333;
`;

const MobileArticleActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid #e0e0e0;
`;

const MobileEditButton = styled.button`
  padding: 0.35rem 0.75rem;
  background-color: #007bff;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.8rem;

  &:hover {
    background-color: #0056b3;
  }
`;

const DiscountBadge = styled.div`
  font-size: 0.8rem;
  color: ${props => props.$isDiscount ? '#d9534f' : '#5cb85c'};
  font-weight: 500;
`;

const TotalSection = styled.div`
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 0.75rem;
  margin-top: 1.5rem;

  @media (min-width: 768px) {
    padding: 1rem;
    margin-top: 2rem;
  }
`;

const TotalRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.35rem 0;
  font-size: ${props => props.isTotal ? '1.1rem' : '0.9rem'};
  font-weight: ${props => props.isTotal ? '700' : '400'};
  color: ${props => props.isTotal ? '#4CAF50' : '#333'};

  @media (min-width: 768px) {
    padding: 0.5rem 0;
    font-size: ${props => props.isTotal ? '1.2rem' : '1rem'};
  }
`;

const NotesSection = styled.div`
  margin-top: 1rem;
  padding: 0.5rem 0.75rem;
  background-color: #f8f9fa;
  border-radius: 3px;
  border-left: 2px solid #6c757d;
`;

const NotesTitle = styled.h3`
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  color: #6c757d;
  margin: 0 0 0.25rem 0;
`;

const NotesContent = styled.p`
  margin: 0;
  color: #666;
  font-size: 0.875rem;
  white-space: pre-line;
  line-height: 1.4;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.5rem;

  @media (min-width: 500px) {
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.75rem;
  }

  @media (min-width: 768px) {
    gap: 1rem;
    margin-top: 2rem;
  }
`;

const Button = styled.button`
  padding: 0.6rem 1rem;
  background-color: ${props => {
    switch(props.variant) {
      case 'primary': return '#4CAF50';
      case 'success': return '#28a745';
      case 'info': return '#17a2b8';
      default: return '#6c757d';
    }
  }};
  color: white;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  cursor: pointer;
  transition: background-color 0.3s;
  white-space: nowrap;

  &:hover {
    background-color: ${props => {
      switch(props.variant) {
        case 'primary': return '#45a049';
        case 'success': return '#218838';
        case 'info': return '#138496';
        default: return '#5a6268';
      }
    }};
  }

  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
    opacity: 0.6;
  }

  @media (min-width: 768px) {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  }
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 3rem;
  color: #666;
`;

// Modal styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 1000;

  @media (min-width: 768px) {
    align-items: center;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px 16px 0 0;
  padding: 1.25rem;
  max-width: 100%;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;

  @media (min-width: 768px) {
    border-radius: 8px;
    padding: 2rem;
    max-width: 600px;
    width: 90%;
    max-height: 80vh;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e0e0e0;

  @media (min-width: 768px) {
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
  }
`;

const ModalTitle = styled.h2`
  margin: 0;
  color: #333;
  font-size: 1.1rem;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
  padding: 0.25rem;

  &:hover {
    color: #333;
  }
`;

const EditForm = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 500;
  color: #555;
`;

const Input = styled.input`
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: #007bff;
  }
`;

const MaterialsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const MaterialItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 4px;
`;

const ModalActions = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e0e0e0;

  @media (min-width: 500px) {
    flex-direction: row;
    justify-content: flex-end;
    gap: 1rem;
    margin-top: 2rem;
  }
`;

// Helper function to group order lines into articles (like TPVDrawer does)
const groupLinesIntoArticles = (lines, order = null) => {
  if (!lines || lines.length === 0) return [];
  
  // Look for a field that groups lines belonging to the same article
  // First, check if there's an orderId or articleId or similar grouping field
  const articlesMap = new Map();
  
  lines.forEach(line => {
    let groupKey;
    
    // Priority 1: Check if line has fingerprint in metadata (for TPV items)
    if (line.metadata && typeof line.metadata === 'object') {
      const metadata = typeof line.metadata === 'string' ? JSON.parse(line.metadata) : line.metadata;
      if (metadata.fingerprint && metadata.calculationIndex) {
        // Use both fingerprint and calculationIndex to ensure unique groups for each TPV item
        groupKey = `${metadata.fingerprint}-${metadata.calculationIndex}`;
      } else if (metadata.fingerprint) {
        groupKey = metadata.fingerprint;
      } else if (metadata.originalOrderId) {
        groupKey = metadata.originalOrderId;
      } else {
        groupKey = generateGroupKey(line);
      }
    }
    // Priority 2: If it's an accessory without metadata, give it its own group
    else if (line.lineType && line.lineType.includes('accesorio')) {
      groupKey = `accessory-${line.id}`;
    }
    // Priority 3: Generate a group key based on dimensions
    else {
      groupKey = generateGroupKey(line);
    }
    
    if (!articlesMap.has(groupKey)) {
      articlesMap.set(groupKey, []);
    }
    articlesMap.get(groupKey).push(line);
  });
  
  // Helper function to generate group key from dimensions
  function generateGroupKey(line) {
    // For accessories or items without dimensions, group them separately
    if (!line.width || !line.height || parseFloat(line.width) === 0 || parseFloat(line.height) === 0) {
      // Check if it's an accessory
      if (line.lineType && line.lineType.includes('accesorio')) {
        return 'accessories-group';
      }
      return `single-${line.id}`;
    }
    
    if (line.width && line.height) {
      const width = parseFloat(line.width);
      const height = parseFloat(line.height);
      if (width > 0 && height > 0) {
        const measurementType = line.measurementType || 'CUSTOM';
        
        // Group items that are close in dimensions (within 15cm tolerance)
        // This handles cases where passepartout has inner dimensions
        // and frame has outer dimensions
        const tolerance = 15; // cm
        const roundedWidth = Math.round(width / tolerance) * tolerance;
        const roundedHeight = Math.round(height / tolerance) * tolerance;
        
        return `${measurementType}-${roundedWidth}x${roundedHeight}`;
      }
    }
    return `single-${line.id}`;
  }
  
  // Convert grouped lines to articles with TPVDrawer-like descriptions
  const articles = Array.from(articlesMap.entries()).map(([groupKey, articleLines]) => {
    // Get basic info from the first line (all lines in the group should have same dimensions)
    const firstLine = articleLines[0];

    // Try to get ORIGINAL dimensions from metadata (what user entered)
    // These are stored when creating with CalculatorPro
    const metadata = typeof firstLine.metadata === 'string'
      ? JSON.parse(firstLine.metadata)
      : (firstLine.metadata || {});

    const hasOriginalDimensions = metadata.originalWidth && metadata.originalHeight;

    // Use original dimensions if available, otherwise fall back to line dimensions
    let displayWidth, displayHeight, displayMeasurementType;

    if (hasOriginalDimensions) {
      displayMeasurementType = metadata.originalMeasurementType || 'INT';

      // For INTERIOR orders, show the glass/passepartout dimensions (the largest)
      // instead of the original interior dimensions
      if (displayMeasurementType === 'INT') {
        const glassPasse = articleLines.filter(l =>
          l.lineType?.includes('cristal') || l.lineType?.includes('passepartout')
        );
        let maxW = 0, maxH = 0;
        glassPasse.forEach(l => {
          const lw = parseFloat(l.width || 0);
          const lh = parseFloat(l.height || 0);
          if (lw * lh > maxW * maxH) { maxW = lw; maxH = lh; }
        });
        if (maxW > 0 && maxH > 0) {
          displayWidth = maxW;
          displayHeight = maxH;
        } else {
          displayWidth = metadata.originalWidth;
          displayHeight = metadata.originalHeight;
        }
      } else {
        displayWidth = metadata.originalWidth;
        displayHeight = metadata.originalHeight;
      }
    } else {
      displayWidth = firstLine.width;
      displayHeight = firstLine.height;
      displayMeasurementType = firstLine.measurementType || '';
    }

    const dimensions = parseFloat(displayWidth) > 0 && parseFloat(displayHeight) > 0
      ? `${displayWidth}x${displayHeight}`
      : '';
    const measurementType = parseFloat(displayWidth) > 0 && parseFloat(displayHeight) > 0
      ? displayMeasurementType
      : '';

    // Build TPV-style description (special rule for LEROY MERLIN)
    let description = '';

    // Check for custom description in first line's metadata
    const customDescription = metadata.customDescription;
    if (customDescription) {
      description = customDescription;
    } else {

    const isLeroyMerlin = order?.client?.name && order.client.name.toUpperCase().includes('LEROY MERLIN');
    const leroyRefs = [order?.clientReference, order?.supplierReference].filter(Boolean);
    const useLeroyRefs = isLeroyMerlin && leroyRefs.length > 0;

    if (useLeroyRefs) {
      description = leroyRefs.join(' - ');
    } else {
      // Start with measurement type and dimensions (shows ORIGINAL dimensions)
      if (measurementType) {
        description += measurementType;
      }
      if (dimensions) {
        description += ` - ${dimensions}`;
      }

      // Add materials like TPVDrawer does
      const components = [];

      const molduras = articleLines.filter(l => l.lineType === 'moldura');
      const passepartouts = articleLines.filter(l => l.lineType?.includes('passepartout'));
      const cristales = articleLines.filter(l => l.lineType?.includes('cristal'));
      const traseras = articleLines.filter(l => l.lineType?.includes('trasera'));
      const accesorios = articleLines.filter(l => l.lineType?.includes('accesorio'));

      if (molduras.length > 0) {
        const molduraNames = molduras.map(m => m.product?.name).filter(Boolean);
        if (molduraNames.length > 0) {
          components.push(`Moldura ${molduraNames.join(' + ')}`);
        }
      }

      if (passepartouts.length > 0) {
        const passepartoutNames = passepartouts.map(p => p.product?.name).filter(Boolean);
        if (passepartoutNames.length > 0) {
          components.push(`Passepartout ${passepartoutNames.join(' + ')}`);
        }
      }

      if (cristales.length > 0) {
        const cristalNames = cristales.map(c => c.product?.name).filter(Boolean);
        if (cristalNames.length > 0) {
          components.push(`Cristal ${cristalNames.join(' + ')}`);
        }
      }

      if (traseras.length > 0) {
        const traseraNames = traseras.map(t => t.product?.name).filter(Boolean);
        if (traseraNames.length > 0) {
          components.push(`Trasera ${traseraNames.join(' + ')}`);
        }
      }

      if (accesorios.length > 0) {
        const accesorioNames = accesorios.map(a => a.product?.name).filter(Boolean);
        if (accesorioNames.length > 0) {
          components.push(`Accesorios ${accesorioNames.join(', ')}`);
        }
      }

      // Join components with " -"
      if (components.length > 0) {
        description += ` -${components.join(' -')}`;
      }

      // Add fingerprint at the end if available
      if (firstLine.fingerprint) {
        description += ` *${firstLine.fingerprint}`;
      }
    }
    } // end of else (no customDescription)

    // Calculate totals for this article
    // The article quantity should be the quantity of the final product, not sum of materials
    // Usually all materials have the same quantity (the article quantity)
    const articleQuantity = Math.max(...articleLines.map(line => line.quantity || 1));

    // Check if any line has a discount/increase applied
    const lineWithDiscount = articleLines.find(line =>
      line.discountPercentage && parseFloat(line.discountPercentage) !== 0
    );
    const hasDiscount = !!lineWithDiscount;
    const discountPercentage = hasDiscount ? parseFloat(lineWithDiscount.discountPercentage) : 0;

    // Total amount is the sum of all material costs for this article
    // IMPORTANT: Prices are already stored WITHOUT VAT in the database
    // Both TPV and CalculatorPro save prices without VAT, so we don't need to adjust them

    let totalAmount = articleLines.reduce((sum, line) => {
      let price = parseFloat(line.totalPrice || 0);

      // Prices are already without VAT, no need to divide by 1.21
      // The backend already handles the VAT conversion when saving

      console.log('Line price calculation in OrderDetail:', {
        productName: line.product?.name || line.metadata?.productName,
        originalTotalPrice: line.totalPrice,
        isPriceManual: line.isPriceManual,
        priceWithoutVAT: price,
        discountPercentage: line.discountPercentage
      });
      return sum + price;
    }, 0);

    // Store the raw calculated amount for later distribution
    const rawTotalAmount = totalAmount;

    // Unit price is total amount divided by article quantity (not material quantities)
    const unitPrice = articleQuantity > 0 ? totalAmount / articleQuantity : 0;

    return {
      id: groupKey,
      description: description || 'Artículo personalizado',
      dimensions: dimensions || '-',
      quantity: articleQuantity,
      unitPrice: unitPrice,
      totalAmount: totalAmount,
      rawTotalAmount: rawTotalAmount,
      lines: articleLines, // Keep reference to original lines for editing
      measurementType: measurementType,
      hasDiscount: hasDiscount,
      discountPercentage: discountPercentage,
      // Store original dimensions for editing (what user entered)
      originalWidth: hasOriginalDimensions ? displayWidth : null,
      originalHeight: hasOriginalDimensions ? displayHeight : null,
      originalMeasurementType: hasOriginalDimensions ? displayMeasurementType : null,
      notes: firstLine.notes || '' // Include line notes
    };
  });

  // Adjust article totals to match the displayed subtotal (minor rounding differences between stored lines and order totals)
  const totalOfAllArticles = articles.reduce((sum, article) => sum + article.rawTotalAmount, 0);

  if (totalOfAllArticles === 0 && order && (order.finalAmount || order.totalAmount)) {
    // If all articles have zero totals, distribute order total equally
    const orderTotal = parseFloat(order.totalAmount || 0) || (parseFloat(order.finalAmount || 0) / 1.21) || 0;
    const articlesCount = articles.length;

    if (articlesCount > 0) {
      articles.forEach(article => {
        article.totalAmount = orderTotal / articlesCount;
        article.unitPrice = article.quantity > 0 ? article.totalAmount / article.quantity : 0;
      });
    }
  } else if (order && articles.length > 0) {
    const isCalculadora = order.orderType ? order.orderType === 'PRO' : (order.fingerprint || '').startsWith('INT-') || (order.fingerprint || '').startsWith('EXT-');

    // Only adjust for PRO orders — TPV prices are displayed as roundToFiveCents(base * 1.21)
    // per article, so adjusting the base would shift the result past a rounding boundary
    if (isCalculadora) {
      const targetBaseAmount = parseFloat(order.totalAmount || 0) || (parseFloat(order.finalAmount || 0) / 1.21) || 0;
      const difference = targetBaseAmount - totalOfAllArticles;

      if (Math.abs(difference) > 0.001 && Math.abs(difference) < 1.0) {
        const largestArticle = articles.reduce((max, article) =>
          article.totalAmount > max.totalAmount ? article : max
        , articles[0]);

        largestArticle.totalAmount = largestArticle.totalAmount + difference;
        largestArticle.unitPrice = largestArticle.quantity > 0 ? largestArticle.totalAmount / largestArticle.quantity : 0;
      }
    }
  }

  return articles;
};

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isPrinting, setIsPrinting] = React.useState(false);
  const [isGeneratingInvoice, setIsGeneratingInvoice] = React.useState(false);
  const [isGeneratingDeliveryNote, setIsGeneratingDeliveryNote] = React.useState(false);
  const [isSyncingOdoo, setIsSyncingOdoo] = React.useState(false);
  const [workOrder, setWorkOrder] = React.useState(null);
  const [editingArticle, setEditingArticle] = React.useState(null);
  const [showEditModal, setShowEditModal] = React.useState(false);
  const [showCalculatorModal, setShowCalculatorModal] = React.useState(false);
  const [availableProducts, setAvailableProducts] = React.useState({});
  const [showClientSelectModal, setShowClientSelectModal] = React.useState(false);
  const [selectedClientId, setSelectedClientId] = React.useState(null);
  // State for adding component within edit modal
  const [showAddComponentUI, setShowAddComponentUI] = React.useState(false);
  const [newComponentType, setNewComponentType] = React.useState('trasera1');
  const [newComponentProductId, setNewComponentProductId] = React.useState('');

  // Helper function to round to 5 cents (for Calculadora Profesional prices)
  const roundToFiveCents = (number) => {
    const scaled = number * 20;
    const nearest = Math.round(scaled);
    if (Math.abs(scaled - nearest) < 0.01) return nearest / 20;
    return Math.ceil(scaled) / 20;
  };

  // Helper to determine if order was created with Calculadora Profesional
  const isCalculadoraPro = (order) => {
    if (!order) return false;
    if (order.orderType) return order.orderType === 'PRO';
    const fp = order.fingerprint || '';
    return fp.startsWith('INT-') || fp.startsWith('EXT-');
  };

  // Helper to calculate final amount with correct rounding based on order type
  const calculateDisplayFinalAmount = (order) => {
    if (!order) return 0;
    const finalAmount = parseFloat(order.finalAmount || order.totalAmount || 0);

    // If manual price is set, use it exactly (no rounding)
    if (order.metadata?.isManualPrice && order.metadata?.manualFinalAmount) {
      return parseFloat(order.metadata.manualFinalAmount);
    }

    // For TPV orders, apply 5-cent rounding to final amount WITH VAT
    // For CalculadoraPro orders, use finalAmount as-is (base already rounded)
    if (!isCalculadoraPro(order)) {
      return roundToFiveCents(finalAmount);
    }

    return finalAmount;
  };

  // Helper to calculate base amount (subtotal without VAT) with correct rounding
  // For TPV orders: calculates base from rounded final (169.23€ → 169.25€ → base 139.88€)
  // For CalculadoraPro: uses totalAmount as-is (base already correctly rounded)
  const calculateDisplayBaseAmount = (order) => {
    if (!order) return 0;

    // CalculadoraPro orders - use totalAmount as-is (base already rounded correctly)
    if (isCalculadoraPro(order)) {
      return parseFloat(order.totalAmount || 0);
    }

    // TPV orders - calculate base from rounded final amount
    // This ensures base matches when final amount is rounded to 5 cents
    const roundedFinalAmount = calculateDisplayFinalAmount(order);
    const baseAmount = roundedFinalAmount / 1.21;

    return baseAmount;
  };

  const { data: order, isLoading, error, refetch } = useQuery({
    queryKey: ['order', id],
    queryFn: () => orderService.getById(id).then(order => {
      console.log('=== ORDER DETAIL DEBUG ===');
      console.log('Order ID:', id);
      console.log('totalAmount:', order.totalAmount);
      console.log('finalAmount:', order.finalAmount);
      console.log('Full order:', order);
      return order;
    }),
  });

  // Mutation for updating order status
  const updateStatusMutation = useMutation({
    mutationFn: ({ orderId, status }) => orderService.updateStatus(orderId, status),
    onSuccess: (data) => {
      queryClient.invalidateQueries(['order', id]);
      queryClient.invalidateQueries(['orders']);
      toast.success('Estado actualizado correctamente');

      const odooSync = data?.odooSync;
      const newStatus = data?.order?.status;
      if (newStatus === 'confirmed' && odooSync) {
        if (odooSync.success && odooSync.id) {
          toast.success(`Sincronizado con Odoo (${odooSync.model}: ${odooSync.id})`);
        } else {
          toast.error(`No se pudo sincronizar con Odoo${odooSync.error ? `: ${odooSync.error}` : ''}`);
        }
      }
    },
    onError: (error) => {
      toast.error('Error al actualizar el estado');
      console.error('Status update error:', error);
    },
  });

  // Fetch work order if order has one
  React.useEffect(() => {
    if (order?.hasWorkOrder) {
      fetchWorkOrder();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.hasWorkOrder]);

  const fetchWorkOrder = async () => {
    try {
      const response = await api.get(`/orders/${id}/work-order`);
      setWorkOrder(response.data.workOrder);
    } catch (error) {
      console.error('Error fetching work order:', error);
    }
  };

  const handlePrint = async () => {
    try {
      // Para pedidos TPV, usar el formato de presupuesto (ticket.php)
      // Para pedidos PRO, usar el formato de pedido normal
      const isTPV = !isCalculadoraPro(order);
      const pdf = isTPV
        ? await orderService.getPresupuestoTPVPDF(id)
        : await orderService.getPDF(id);
      const url = window.URL.createObjectURL(pdf);
      const link = document.createElement('a');
      link.href = url;
      link.download = isTPV
        ? `presupuesto-${order.orderNumber}.pdf`
        : `pedido-${order.orderNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Error al descargar el PDF');
    }
  };

  // Handler para imprimir el presupuesto CON NOTAS (notas de cada artículo + observación general)
  const handlePrintPresupuestoNotas = async () => {
    try {
      const pdf = await orderService.getPresupuestoTPVPDF(id, { notes: true });
      const url = window.URL.createObjectURL(pdf);
      const link = document.createElement('a');
      link.href = url;
      link.download = `presupuesto-notas-${order.orderNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading presupuesto con notas:', error);
      toast.error('Error al descargar el presupuesto con notas');
    }
  };

  // Handler para imprimir ticket (solo TPV) - sincroniza con Odoo y añade QR
  const handlePrintTicket = async () => {
    try {
      toast.info('Generando ticket con TicketBAI...');
      const pdf = await orderService.getTicketTPVPDF(id);
      const url = window.URL.createObjectURL(pdf);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ticket-${order.orderNumber}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('Ticket generado correctamente');
    } catch (error) {
      console.error('Error downloading ticket PDF:', error);
      toast.error('Error al generar el ticket');
    }
  };

  const handleGenerateInvoice = async () => {
    // Validar que haya un cliente asignado antes de facturar
    // VENTA TIENDA se considera como "sin cliente" para facturación
    const clientName = order.client?.name?.toUpperCase()?.trim() || '';
    const isVentaTienda = clientName === 'VENTA TIENDA' || clientName.includes('VENTA TIENDA');
    console.log('Invoice validation - Client:', order.client?.name, 'isVentaTienda:', isVentaTienda);
    if (!order.client || !order.client.id || isVentaTienda) {
      toast.warning('Debe seleccionar un cliente antes de generar la factura');
      setShowClientSelectModal(true);
      return;
    }

    try {
      setIsGeneratingInvoice(true);
      // Primero generar la factura
      const response = await api.post(`/orders/${id}/generate-invoice`);
      
      queryClient.invalidateQueries(['orders']);
      queryClient.invalidateQueries(['order', id]);
      queryClient.invalidateQueries(['invoices']);

      // Luego descargar el PDF usando el mismo método que los otros botones
      if (response.data.pdfUrl && response.data.filename) {
        const pdfResponse = await api.get(response.data.pdfUrl, {
          responseType: 'blob'
        });

        const url = window.URL.createObjectURL(pdfResponse.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename || `factura-${order.orderNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Factura generada y descargada exitosamente');
      } else {
        toast.success('Factura generada exitosamente');
      }
    } catch (error) {
      console.error('Generate invoice error:', error);
      // Si el backend indica que falta cliente, mostrar modal de selección
      if (error.response?.data?.needsClient) {
        toast.warning('Debe seleccionar un cliente antes de generar la factura');
        setShowClientSelectModal(true);
      } else if (error.response?.data?.error) {
        toast.error(`Error: ${error.response.data.error}`);
      } else if (error.response?.status === 401) {
        toast.error('Sesión expirada. Por favor, inicie sesión nuevamente.');
        navigate('/login');
      } else {
        toast.error('Error al generar la factura');
      }
    } finally {
      setIsGeneratingInvoice(false);
    }
  };

  const handleGenerateDeliveryNote = async () => {
    try {
      setIsGeneratingDeliveryNote(true);
      // Primero generar el albarán
      const response = await api.post(`/orders/${id}/generate-delivery-note`);
      
      // Luego descargar el PDF usando el mismo método que los otros botones
      if (response.data.pdfUrl && response.data.filename) {
        const pdfResponse = await api.get(response.data.pdfUrl, {
          responseType: 'blob'
        });
        
        const url = window.URL.createObjectURL(pdfResponse.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.data.filename || `albaran-${order.orderNumber}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success('Albarán generado y descargado exitosamente');
      } else {
        toast.success('Albarán generado exitosamente');
      }

      queryClient.invalidateQueries(['orders']);
      queryClient.invalidateQueries(['order', id]);
      queryClient.invalidateQueries(['deliveryNotes']);

      navigate('/delivery-notes');
    } catch (error) {
      console.error('Generate delivery note error:', error);
      if (error.response?.data?.error) {
        toast.error(`Error: ${error.response.data.error}`);
      } else if (error.response?.status === 401) {
        toast.error('Sesión expirada. Por favor, inicie sesión nuevamente.');
        navigate('/login');
      } else {
        toast.error('Error al generar el albarán');
      }
    } finally {
      setIsGeneratingDeliveryNote(false);
    }
  };

  const handlePrintWorkOrder = async () => {
    try {
      setIsPrinting(true);
      // Get work order PDF directly without creating work order record
      const pdf = await orderService.getWorkOrderPDF(id);
      const url = window.URL.createObjectURL(pdf);
      const link = document.createElement('a');
      link.href = url;
      link.download = `orden-trabajo-${order.orderNumber}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Orden de trabajo descargada');
    } catch (error) {
      console.error('Print work order error:', error);
      toast.error('Error al generar orden de trabajo');
    } finally {
      setIsPrinting(false);
    }
  };

  const handleSyncOdoo = async () => {
    try {
      setIsSyncingOdoo(true);
      const result = await orderService.syncOdoo(id);
      queryClient.invalidateQueries(['order', id]);
      queryClient.invalidateQueries(['orders']);

      const odooSync = result?.odooSync;
      if (odooSync?.success && odooSync?.id) {
        toast.success(`Sincronizado con Odoo (${odooSync.model}: ${odooSync.id})`);
      } else {
        toast.error(`No se pudo sincronizar con Odoo${odooSync?.error ? `: ${odooSync.error}` : ''}`);
      }
    } catch (error) {
      console.error('Manual Odoo sync error:', error);
      toast.error('Error al sincronizar con Odoo');
    } finally {
      setIsSyncingOdoo(false);
    }
  };

  const handleEditArticle = async (article) => {
    try {
      const isProOrder = isCalculadoraPro(order);
      
      // Load available products when opening modal
      let products = availableProducts;
      if (Object.keys(availableProducts).length === 0) {
        try {
          const response = await calculatorService.getProducts();
          products = response.products || {};
          setAvailableProducts(products);
        } catch (err) {
          console.error('Error loading products:', err);
          toast.error('Error al cargar los productos disponibles');
        }
      }
      
      // Ensure each line has the correct product information loaded
      const linesWithProducts = article.lines.map(line => {
        if (line.productId && products) {
          // Find the product in the appropriate category (productId is UUID string)
          let product = null;
          if (line.lineType === 'moldura' && products.molduras) {
            product = products.molduras.find(p => p.id === line.productId);
          } else if (line.lineType && line.lineType.includes('passepartout') && products.passepartout) {
            product = products.passepartout.find(p => p.id === line.productId);
          } else if (line.lineType && line.lineType.includes('cristal') && products.cristales) {
            product = products.cristales.find(p => p.id === line.productId);
          } else if (line.lineType && line.lineType.includes('trasera') && products.traseras) {
            product = products.traseras.find(p => p.id === line.productId);
          } else if (line.lineType && line.lineType.includes('accesorio') && products.accesorios) {
            product = products.accesorios.find(p => p.id === line.productId);
          }
          
          // If product found in availableProducts, use it with proper pricing info
          if (product) {
            return {
              ...line,
              product: {
                ...product,
                basePrice: parseFloat(product.basePrice) || parseFloat(product.list_price) || 0
              }
            };
          }
        }

        // FALLBACK: If product not found in availableProducts but line already has product from backend,
        // ensure it has proper pricing info (basePrice parsed as number)
        if (line.product) {
          return {
            ...line,
            product: {
              ...line.product,
              basePrice: parseFloat(line.product.basePrice) || parseFloat(line.product.list_price) || 0,
              // Ensure unit is set (default to m2 for non-moldura products)
              unit: line.product.unit || (line.lineType === 'moldura' ? 'ml' : 'm2')
            }
          };
        }

        // Return line as-is if no product at all
        return line;
      });
      
      // Determine the "base" (outer) dimensions for this article.
      // For Pro orders we persist deltas in line.metadata so re-edits are reversible.
      let baseW = 0;
      let baseH = 0;

      if (isProOrder) {
        const firstWithMeta = linesWithProducts.find(l => l.metadata?.editBaseW && l.metadata?.editBaseH);
        if (firstWithMeta) {
          baseW = parseFloat(firstWithMeta.metadata.editBaseW) || 0;
          baseH = parseFloat(firstWithMeta.metadata.editBaseH) || 0;
        }
      }

      if (!baseW || !baseH) {
        const numericDims = linesWithProducts
          .filter(l => l.width && l.height && parseFloat(l.width) > 0 && parseFloat(l.height) > 0)
          .map(l => ({ w: parseFloat(l.width), h: parseFloat(l.height), lineType: l.lineType }));

        const molduraDim = numericDims.find(d => d.lineType === 'moldura');
        if (molduraDim) {
          baseW = molduraDim.w;
          baseH = molduraDim.h;
        } else if (numericDims.length > 0) {
          baseW = Math.max(...numericDims.map(d => d.w));
          baseH = Math.max(...numericDims.map(d => d.h));
        } else {
          const dimensionParts = article.dimensions.split('x');
          baseW = dimensionParts[0] ? parseFloat(dimensionParts[0]) : 0;
          baseH = dimensionParts[1] ? parseFloat(dimensionParts[1]) : 0;
        }
      }

      // Attach per-line deltas so edits remain reversible.
      const linesWithDeltas = linesWithProducts.map(line => {
        const lw = parseFloat(line.width || 0);
        const lh = parseFloat(line.height || 0);

        let deltaW = 0;
        let deltaH = 0;
        if (isProOrder && line.metadata?.deltaWidth !== undefined && line.metadata?.deltaHeight !== undefined) {
          deltaW = parseFloat(line.metadata.deltaWidth) || 0;
          deltaH = parseFloat(line.metadata.deltaHeight) || 0;
        } else {
          deltaW = lw && baseW ? (lw - baseW) : 0;
          deltaH = lh && baseH ? (lh - baseH) : 0;
        }

        return {
          ...line,
          _originalWidth: lw,
          _originalHeight: lh,
          _deltaWidth: deltaW,
          _deltaHeight: deltaH
        };
      });

      // Apply 5-cent rounding to totalAmount if it's a Calculadora Profesional order
      const totalAmount = parseFloat(article.totalAmount) || 0;
      const roundedTotalAmount = isCalculadoraPro(order) ? roundToFiveCents(totalAmount) : totalAmount;

      // For edit modal: pass original (input) dimensions so INT orders don't grow on each edit
      const firstLineMeta = linesWithProducts[0]?.metadata || {};
      const origW = parseFloat(firstLineMeta.originalWidth || 0);
      const origH = parseFloat(firstLineMeta.originalHeight || 0);

      const articleWithDimensions = {
        ...article,
        width: baseW,
        height: baseH,
        ...(origW > 0 && origH > 0 ? { originalWidth: origW, originalHeight: origH } : {}),
        dimensions: `${baseW}x${baseH}`,
        // Cache margin multiplier for modal price previews
        marginMultiplier: isProOrder
          ? ((100 + (parseFloat(order.metadata?.clientMargin || 0))) / 100)
          : 1,
        lines: linesWithDeltas,
        totalAmount: roundedTotalAmount,
        unitPrice: article.quantity > 0 ? roundedTotalAmount / article.quantity : 0
      };

      setEditingArticle(articleWithDimensions);
      // Use calculator modal for editing
      setShowCalculatorModal(true);

    } catch (error) {
      console.error('Edit article error:', error);
      toast.error('Error al editar el artículo');
    }
  };

  // Handle save from Calculator Edit Modal
  const handleCalculatorEditSave = async (editResult) => {
    try {
      console.log('handleCalculatorEditSave received:', editResult);

      if (!editResult || !editResult.calculation) {
        toast.error('No hay datos de cálculo para guardar');
        return false;
      }

      const isProOrder = isCalculadoraPro(order);
      const vatMultiplier = 1.21;
      const calculation = editResult.calculation;
      const materials = editResult.materials || {};

      // Get the original article lines to update
      const originalLines = editingArticle?.lines || [];

      // Map calculation items to line updates
      const calculationItems = calculation.items || editResult.items || [];

      // The calculation returns the EXTERIOR dimensions (after applying passepartout)
      // These are the dimensions that should be stored in the database
      const calculatedWidth = calculation.width || editResult.formData.width;
      const calculatedHeight = calculation.height || editResult.formData.height;

      console.log('Saving with dimensions:', {
        formWidth: editResult.formData.width,
        formHeight: editResult.formData.height,
        calculatedWidth,
        calculatedHeight,
        measurementType: editResult.formData.measurementType
      });

      // Check if manual price is enabled
      const isManualPrice = editResult.formData.isManualPrice;
      const manualTotal = editResult.formData.manualPrice;

      // Calculate the adjustment factor for manual price
      let priceAdjustmentFactor = 1;
      if (isManualPrice && manualTotal !== null && calculation.originalTotal) {
        priceAdjustmentFactor = manualTotal / calculation.originalTotal;
      } else if (isManualPrice && manualTotal !== null) {
        const calcTotal = calculationItems.reduce((sum, item) => sum + (item.totalPrice || 0), 0);
        if (calcTotal > 0) {
          priceAdjustmentFactor = manualTotal / calcTotal;
        }
      }

      // Update discount and manual price metadata BEFORE updating lines
      // so the backend recalculation uses the correct discount percentage
      const newDiscount = parseFloat(editResult.formData.discount) || 0;
      try {
        const patchData = {
          discountPercentage: newDiscount,
          // El precio manual se aplica a la LÍNEA editada (vía isPriceManual + factor de ajuste),
          // NO al total del pedido. Limpiamos siempre manualFinalAmount para que el total del pedido
          // sea la SUMA de todas las líneas. (Antes, editar una línea de un pedido con varias líneas
          // ponía el total del pedido = precio de esa única línea.) El backend recalcula el total
          // desde las líneas; con manualFinalAmount vacío, el override del backend se salta.
          metadata: { manualFinalAmount: null, isManualPrice: false }
        };
        await api.patch(`/orders/${id}`, patchData);
        console.log('Updated order discount and manual price:', patchData);
      } catch (metaError) {
        console.error('Failed to update order metadata/discount:', metaError);
      }

      // Create tracking sets for matching
      const usedItemIndices = new Set();
      const matchedOriginalLineIds = new Set();
      const saveErrors = [];

      // Helper function to get base type from lineType
      const getBaseType = (lineType) => {
        if (!lineType) return 'other';
        if (lineType === 'moldura') return 'moldura';
        if (lineType.includes('passepartout')) return 'passepartout';
        if (lineType.includes('cristal')) return 'cristal';
        if (lineType.includes('trasera')) return 'trasera';
        if (lineType.includes('accesorio')) return 'accesorio';
        return 'other';
      };

      // Helper to get index within type (e.g., passepartout1 -> 0, passepartout2 -> 1)
      const getTypeIndex = (lineType) => {
        const match = lineType?.match(/(\d+)$/);
        return match ? parseInt(match[1]) - 1 : 0;
      };

      // Group calculation items by type for better matching
      const itemsByType = {};
      calculationItems.forEach((item, idx) => {
        const type = item.type || 'other';
        if (!itemsByType[type]) {
          itemsByType[type] = [];
        }
        itemsByType[type].push({ ...item, originalIndex: idx });
      });

      // Process each original line and find matching updated item
      for (const originalLine of originalLines) {
        if (!originalLine.id) continue;

        const baseType = getBaseType(originalLine.lineType);
        const typeIndex = getTypeIndex(originalLine.lineType);

        // Find matching item from calculation by type and index
        let matchingItem = null;
        const typeItems = itemsByType[baseType] || [];
        if (typeItems[typeIndex] && !usedItemIndices.has(typeItems[typeIndex].originalIndex)) {
          matchingItem = typeItems[typeIndex];
          usedItemIndices.add(typeItems[typeIndex].originalIndex);
        } else {
          // Fallback: find first unused item of same type
          for (const item of typeItems) {
            if (!usedItemIndices.has(item.originalIndex)) {
              matchingItem = item;
              usedItemIndices.add(item.originalIndex);
              break;
            }
          }
        }

        // No matching calculation item: this line was removed by the user.
        // Skip update so it can be deleted in step 2.
        if (!matchingItem) {
          console.log(`No matching item for line ${originalLine.id} (${originalLine.lineType}), will delete`);
          continue;
        }

        // Mark as matched as soon as we found an item.
        // This prevents accidental deletion if PUT fails transiently.
        matchedOriginalLineIds.add(originalLine.id);

        // Use the dimensions from the matching item (which are the calculated exterior dimensions)
        // or fallback to the calculation's dimensions
        const itemWidth = matchingItem?.width || calculatedWidth;
        const itemHeight = matchingItem?.height || calculatedHeight;

        // Parse existing metadata if it's a string
        const existingMeta = typeof originalLine.metadata === 'string'
          ? JSON.parse(originalLine.metadata || '{}')
          : (originalLine.metadata || {});

        // Prepare update data with CALCULATED dimensions (exterior), not form dimensions (interior)
        const updateData = {
          quantity: editResult.formData.units,
          notes: editResult.formData.notes || '', // Save line notes
          // ALWAYS store original dimensions in metadata for display purposes
          metadata: {
            ...existingMeta,
            // Store original dimensions (what user entered) for display
            originalWidth: editResult.formData.width,
            originalHeight: editResult.formData.height,
            originalMeasurementType: editResult.formData.measurementType,
            isManualPrice: isManualPrice || false,
            customDescription: editResult.formData.description || null
          }
        };

        // Solo enviar width/height si son números válidos. El backend valida isNumeric()
        // y su .optional() NO salta null (solo undefined), así que enviar null da 400 al
        // editar líneas sin dimensiones (p.ej. componentes / precio manual). Además
        // parseFloat(null) sería NaN. Si no hay dimensión, se omite y se conserva la actual.
        if (itemWidth != null && !isNaN(itemWidth)) updateData.width = itemWidth;
        if (itemHeight != null && !isNaN(itemHeight)) updateData.height = itemHeight;

        // Update price if we have a matching item
        if (matchingItem) {
          // Apply price adjustment factor if manual price is enabled
          const displayedPrice = matchingItem.totalPrice || 0;
          const adjustedDisplayedPrice = isManualPrice ? displayedPrice * priceAdjustmentFactor : displayedPrice;
          const adjustedBasePrice = isProOrder ? adjustedDisplayedPrice : (adjustedDisplayedPrice / vatMultiplier);
          const units = editResult.formData.units || 1;
          updateData.unitPrice = adjustedBasePrice;
          updateData.totalPrice = adjustedBasePrice * units;

          // Update productId if changed
          if (matchingItem.productId) {
            updateData.productId = matchingItem.productId;
          }

          // For Pro orders, add additional metadata
          if (isProOrder) {
            updateData.isPriceManual = true;
            updateData.metadata = {
              ...updateData.metadata,
              editBaseW: calculatedWidth,
              editBaseH: calculatedHeight,
              deltaWidth: originalLine._deltaWidth || 0,
              deltaHeight: originalLine._deltaHeight || 0
            };
          }
        }

        // Also check materials for productId if not found in matchingItem
        if (!updateData.productId) {
          const materialsKey = baseType === 'moldura' ? 'molduras' :
                              baseType === 'passepartout' ? 'passepartouts' :
                              baseType === 'cristal' ? 'cristales' :
                              baseType === 'trasera' ? 'traseras' :
                              baseType === 'accesorio' ? 'accesorios' : null;

          if (materialsKey && materials[materialsKey]) {
            const materialItem = materials[materialsKey][typeIndex];
            if (materialItem) {
              // passepartouts have { productId, width }, others are just productId string
              const productId = typeof materialItem === 'object' ? materialItem.productId : materialItem;
              if (productId) {
                updateData.productId = productId;
              }
            }
          }
        }

        console.log(`Updating line ${originalLine.id}:`, updateData);
        // Call API to update line
        try {
          await orderService.updateLine(id, originalLine.id, updateData);
        } catch (putError) {
          console.error(`PUT failed for line ${originalLine.id}:`, putError);
          saveErrors.push({
            action: 'update',
            lineId: originalLine.id,
            error: putError?.response?.data || putError?.message || 'Error desconocido'
          });
        }
      }

      // Step 2: Delete original lines that were removed (no matching calculation item)
      for (const originalLine of originalLines) {
        if (originalLine.id && !matchedOriginalLineIds.has(originalLine.id)) {
          console.log(`Deleting removed line ${originalLine.id} (${originalLine.lineType})`);
          try {
            await api.delete(`/orders/${id}/lines/${originalLine.id}`);
          } catch (delError) {
            console.error(`DELETE failed for line ${originalLine.id}:`, delError);
            saveErrors.push({
              action: 'delete',
              lineId: originalLine.id,
              error: delError?.response?.data || delError?.message || 'Error desconocido'
            });
          }
        }
      }

      // Step 3: Create new lines for calculation items not matched to existing lines
      const articleFingerprint = originalLines[0]?.metadata?.fingerprint || editingArticle?.fingerprint;
      for (const [type, items] of Object.entries(itemsByType)) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          if (usedItemIndices.has(item.originalIndex)) continue; // Already matched

          const lineType = type === 'moldura' ? 'moldura' : (item.typeWithIndex || `${type}${i + 1}`);

          let productId = item.productId;
          if (!productId) {
            const materialsKey = type === 'moldura' ? 'molduras' :
                                type === 'passepartout' ? 'passepartouts' :
                                type === 'cristal' ? 'cristales' :
                                type === 'trasera' ? 'traseras' :
                                type === 'accesorio' ? 'accesorios' : null;
            if (materialsKey && materials[materialsKey] && materials[materialsKey][i]) {
              const materialItem = materials[materialsKey][i];
              productId = typeof materialItem === 'object' ? materialItem.productId : materialItem;
            }
          }

          if (productId) {
            // Use the price from the calculation (already includes client margin)
            const displayedPrice = item.totalPrice || 0;
            const adjustedDisplayedPrice = isManualPrice ? displayedPrice * priceAdjustmentFactor : displayedPrice;
            const adjustedBasePrice = isProOrder ? adjustedDisplayedPrice : (adjustedDisplayedPrice / vatMultiplier);

            console.log(`Creating new ${type} line: lineType=${lineType}, productId=${productId}, totalPrice=${adjustedBasePrice}`);
            try {
              const units = editResult.formData.units || 1;
              await api.post(`/orders/${id}/lines`, {
                lineType,
                productId,
                width: item.width || calculatedWidth,
                height: item.height || calculatedHeight,
                quantity: units,
                notes: editResult.formData.notes || '',
                articleFingerprint,
                unitPrice: adjustedBasePrice,
                totalPrice: adjustedBasePrice * units
              });
            } catch (postError) {
              console.error(`POST failed for new ${type} line:`, postError);
              saveErrors.push({
                action: 'create',
                lineType,
                error: postError?.response?.data || postError?.message || 'Error desconocido'
              });
            }
          } else {
            console.warn(`Skipping new ${type} line - no productId found`);
          }
        }
      }

      if (saveErrors.length > 0) {
        console.error('Article save had partial failures:', saveErrors);
        toast.error(`No se pudieron guardar ${saveErrors.length} cambio(s). Revisa los datos e inténtalo de nuevo.`);
        return false;
      }

      // Update order fields if changed (client, references)
      const orderPatch = {};
      const newClientId = editResult.formData.clientId;
      if (newClientId && newClientId !== order.clientId) {
        orderPatch.clientId = newClientId;
      }
      if (editResult.formData.supplierReference !== undefined) {
        orderPatch.supplierReference = editResult.formData.supplierReference;
      }
      if (editResult.formData.clientReference !== undefined) {
        orderPatch.clientReference = editResult.formData.clientReference;
      }
      // Limpiar el override de total manual del pedido. El precio manual de un artículo se aplica
      // a SUS líneas (vía priceAdjustmentFactor), NO al total del pedido. Si queda un
      // manualFinalAmount (p.ej. de la venta TPV), el display lo REPARTE entre los artículos
      // (precios raros tipo 2.42 / 4 / 20) y el total muestra ese override en vez de la suma de
      // líneas. Igual que hacen los otros handlers de edición de precio.
      orderPatch.metadata = { ...(order.metadata || {}), manualFinalAmount: null, isManualPrice: false };
      if (Object.keys(orderPatch).length > 0) {
        try {
          await api.patch(`/orders/${id}`, orderPatch);
        } catch (patchError) {
          console.error('Failed to update order fields:', patchError);
          toast.error('Artículo guardado pero no se pudieron actualizar las referencias');
        }
      }

      toast.success(isManualPrice ? 'Artículo actualizado con precio manual' : 'Artículo actualizado correctamente');
      setShowCalculatorModal(false);
      setEditingArticle(null);
      refetch();
      queryClient.invalidateQueries(['orders']);
      return true;

    } catch (error) {
      console.error('Calculator edit save error:', error);
      toast.error('Error al guardar los cambios');
      return false;
    }
  };


  const handleCloseModal = () => {
    setShowEditModal(false);
    setEditingArticle(null);
    setShowAddComponentUI(false);
    setNewComponentProductId('');
  };

  const handleSaveArticle = async (updatedArticle) => {
    try {
      if (!updatedArticle?.lines || updatedArticle.lines.length === 0) {
        console.warn('handleSaveArticle called with no lines', updatedArticle);
        toast.error('No se encontraron líneas para actualizar');
        return;
      }
      const isProOrder = isCalculadoraPro(order);
      const marginMultiplier = isProOrder
        ? ((100 + (parseFloat(order.metadata?.clientMargin || 0))) / 100)
        : 1;

      // Separate existing lines from new lines
      const existingLines = updatedArticle.lines.filter(l => !l.isNew);
      const newLines = updatedArticle.lines.filter(l => l.isNew);

      // Update each EXISTING line SEQUENTIALLY to avoid race conditions in backend recalculation
      for (const line of existingLines) {
        // Prepare update data for each line
        const updateData = {
          quantity: updatedArticle.quantity // Use the article quantity for all lines
        };

        // Only include productId if it's actually set (not null or empty)
        if (line.productId) {
          updateData.productId = line.productId;
        }

        // For Calculadora Pro, always send unitPrice with margin so backend
        // recalculation uses correct per-unit value even after previous edits.
        if (isProOrder && line.product?.basePrice !== undefined) {
          const basePrice = parseFloat(line.product.basePrice) || 0;
          updateData.unitPrice = basePrice * marginMultiplier;
        }

        if (isProOrder) {
          // Calculadora Pro: persist exact modal totals as manual prices and store deltas in metadata
          // so future edits can restore original inner/outer relationships.
          updateData.width = line.width;
          updateData.height = line.height;
          updateData.totalPrice = line.totalPrice;
          updateData.isPriceManual = true;
          updateData.metadata = {
            editBaseW: updatedArticle.width,
            editBaseH: updatedArticle.height,
            deltaWidth: line._deltaWidth || 0,
            deltaHeight: line._deltaHeight || 0
          };
        } else {
          // TPV: keep manual line prices to preserve cash-register rounding.
          updateData.width = updatedArticle.width;
          updateData.height = updatedArticle.height;
          updateData.totalPrice = line.totalPrice;
          updateData.isPriceManual = true;
        }

        // Call API to update each line sequentially
        await orderService.updateLine(id, line.id, updateData);
      }

      // Create NEW lines for added components
      for (const newLine of newLines) {
        await api.post(`/orders/${order.id}/lines`, {
          lineType: newLine.lineType,
          productId: newLine.productId,
          width: newLine.width,
          height: newLine.height,
          notes: newLine.notes || null,
          articleFingerprint: updatedArticle.fingerprint,
          quantity: updatedArticle.quantity
        });
      }

      toast.success('Artículo actualizado correctamente');
      setShowEditModal(false);
      setEditingArticle(null);
      setShowAddComponentUI(false);
      refetch(); // Refresh the order data
      queryClient.invalidateQueries(['orders']); // Refresh the orders list
    } catch (error) {
      console.error('Save article error:', error);
      toast.error('Error al guardar los cambios');
    }
  };

  // Helper function to get products for a specific material type
  const getProductsForLineType = (lineType) => {
    if (!lineType || Object.keys(availableProducts).length === 0) return [];
    
    let category = '';
    if (lineType === 'moldura') {
      category = 'molduras';
    } else if (lineType.includes('passepartout')) {
      category = 'passepartout';
    } else if (lineType.includes('cristal')) {
      category = 'cristales';
    } else if (lineType.includes('trasera')) {
      category = 'traseras';
    } else if (lineType.includes('accesorio')) {
      category = 'accesorios';
    }
    
    return availableProducts[category] || [];
  };

  // Line type options for adding new components
  const lineTypeOptions = [
    { value: 'moldura', label: 'Moldura' },
    { value: 'passepartout1', label: 'Passepartout' },
    { value: 'cristal1', label: 'Cristal' },
    { value: 'trasera1', label: 'Trasera' },
    { value: 'accesorio1', label: 'Accesorio' }
  ];

  // Handler to add a new component to the editing article
  const handleAddComponentToArticle = () => {
    if (!editingArticle || !newComponentProductId) return;

    const productsForType = getProductsForLineType(newComponentType);
    const selectedProduct = productsForType.find(p => p.id === newComponentProductId);
    if (!selectedProduct) return;

    const marginMultiplier = editingArticle.marginMultiplier || 1;
    const basePrice = parseFloat(selectedProduct.basePrice) || 0;
    const effectiveUnit = basePrice * marginMultiplier;
    const quantity = editingArticle.quantity || 1;
    const width = parseFloat(editingArticle.width) || 0;
    const height = parseFloat(editingArticle.height) || 0;

    // Calculate price based on line type
    let newTotalPrice = effectiveUnit * quantity; // default for accessories
    const isMoldura = newComponentType === 'moldura';
    const isAccessory = newComponentType.includes('accesorio');

    if (isMoldura) {
      const pW = Math.max(width, 20);
      const pH = Math.max(height, 20);
      const perimeter = ((pW + pH) * 2) / 100;
      newTotalPrice = perimeter * effectiveUnit * quantity;
    } else if (!isAccessory) {
      // Square meters for passepartout, cristal, trasera (minimum 20x20 for pricing)
      const pW = Math.max(width, 20);
      const pH = Math.max(height, 20);
      const area = (pW * pH) / 10000;
      newTotalPrice = area * effectiveUnit * quantity;
    }

    const newLine = {
      id: `new-${Date.now()}`,
      isNew: true,
      lineType: newComponentType,
      productId: newComponentProductId,
      product: {
        ...selectedProduct,
        basePrice: basePrice
      },
      width: width,
      height: height,
      totalPrice: newTotalPrice,
      _deltaWidth: 0,
      _deltaHeight: 0
    };

    const updatedLines = [...editingArticle.lines, newLine];
    const summedTotal = updatedLines.reduce((sum, l) => sum + (parseFloat(l.totalPrice) || 0), 0);
    const newTotalAmount = isCalculadoraPro(order) ? roundToFiveCents(summedTotal) : summedTotal;

    setEditingArticle({
      ...editingArticle,
      lines: updatedLines,
      totalAmount: newTotalAmount,
      unitPrice: quantity > 0 ? newTotalAmount / quantity : 0
    });

    // Reset the add component UI
    setShowAddComponentUI(false);
    setNewComponentProductId('');
  };

  // Helper function to format line type for display
  const formatLineTypeTitle = (lineType) => {
    if (!lineType) return '';
    
    // Remove numbers and clean up the line type
    if (lineType === 'moldura' || lineType.startsWith('moldura')) {
      return 'Moldura';
    } else if (lineType.includes('passepartout')) {
      return 'Passepartout';
    } else if (lineType.includes('cristal')) {
      return 'Cristal';
    } else if (lineType.includes('trasera')) {
      return 'Trasera';
    } else if (lineType.includes('accesorio')) {
      return 'Accesorio';
    }
    
    return lineType;
  };

  if (isLoading) {
    return (
      <DetailContainer>
        <LoadingState>Cargando pedido...</LoadingState>
      </DetailContainer>
    );
  }

  const getWorkOrderStatusLabel = (status) => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'in_production':
        return 'En Producción';
      case 'completed':
        return 'Completado';
      default:
        return status;
    }
  };

  if (error || !order) {
    toast.error('Error al cargar el pedido');
    navigate('/orders');
    return null;
  }

  const isDelivered = order.status === 'delivered';
  const isInvoiced = order.status === 'invoiced';
  const canGenerateInvoice = order.status === 'delivered';
  const canGenerateDeliveryNote = order.status === 'ready';

  // Block editing if order has delivery notes (albaranes)
  const canEdit = !order.hasDeliveryNotes && !isInvoiced;
  const editBlockedReason = order.hasDeliveryNotes
    ? 'No se puede editar: el pedido ya tiene albarán(es) creado(s)'
    : isInvoiced
    ? 'No se puede editar: el pedido ya está facturado'
    : null;

  return (
    <DetailContainer>
      <BackButton onClick={() => navigate('/orders')}>
        ← Volver a pedidos
      </BackButton>

      {!canEdit && editBlockedReason && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#fff3cd',
          border: '1px solid #ffc107',
          borderRadius: '4px',
          marginBottom: '1rem',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          color: '#856404'
        }}>
          <span style={{ fontSize: '1.2rem' }}>⚠️</span>
          <span style={{ fontWeight: 500 }}>{editBlockedReason}</span>
        </div>
      )}

      <OrderCard>
        <OrderHeader>
          <OrderInfo>
            <OrderNumber>Pedido #{order.orderNumber}</OrderNumber>
            <OrderDate>{formatDate(order.createdAt)}</OrderDate>
          </OrderInfo>
        </OrderHeader>

        <Section>
          <SectionTitle>Información del Cliente</SectionTitle>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Cliente:</InfoLabel>
              <InfoValue>{order.client?.name || 'Sin cliente'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Email:</InfoLabel>
              <InfoValue>{order.client?.email || 'No especificado'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Teléfono:</InfoLabel>
              <InfoValue>{order.client?.phone || 'No especificado'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Dirección:</InfoLabel>
              <InfoValue>
                {(() => {
                  const { street, city, zip } = order.client || {};
                  if (!street && !city && !zip) return 'No especificada';
                  const parts = [street, city, zip].filter(Boolean);
                  return parts.join(', ');
                })()}
              </InfoValue>
            </InfoItem>
          </InfoGrid>
        </Section>

        {/* Sección de Notas/Observaciones */}
        {order.notes && order.notes.trim() && (
          <NotesSection>
            <NotesTitle>Observaciones</NotesTitle>
            <NotesContent>{order.notes}</NotesContent>
          </NotesSection>
        )}

        {order.metadata?.targetDeliveryDate && (
          <div style={{
            padding: '0.75rem 1rem',
            background: '#fff3e0',
            borderRadius: '8px',
            marginBottom: '1rem',
            fontSize: '0.9rem',
            color: '#e65100',
            fontWeight: 600
          }}>
            📅 Fecha de entrega: {new Date(order.metadata.targetDeliveryDate).toLocaleDateString('es-ES')}
          </div>
        )}

        {/* Pre-compute articles with adjusted display prices for manual price support */}
        {(() => {
          const computedArticles = order.lines && order.lines.length > 0
            ? groupLinesIntoArticles(order.lines, order)
            : [];
          const isTPV = !isCalculadoraPro(order);
          const hasManualPrice = order.metadata?.isManualPrice && order.metadata?.manualFinalAmount;

          // Compute display price for each article and adjust for manual price
          const articlePriceMap = {};
          let rawPriceSum = 0;
          computedArticles.forEach((article) => {
            const qty = article.quantity > 0 ? article.quantity : 1;
            const unitBase = article.totalAmount / qty;
            const rawPrice = isTPV
              ? roundToFiveCents(unitBase * 1.21) * qty
              : roundToFiveCents(unitBase) * qty;
            articlePriceMap[article.id] = rawPrice;
            rawPriceSum += rawPrice;
          });

          // Proportional adjustment when manual price differs from raw calculation
          const manualTotal = hasManualPrice ? parseFloat(order.metadata.manualFinalAmount) : null;
          if (isTPV && manualTotal && rawPriceSum > 0 && Math.abs(rawPriceSum - manualTotal) > 0.001) {
            const ratio = manualTotal / rawPriceSum;
            computedArticles.forEach((article) => {
              articlePriceMap[article.id] = Math.round(articlePriceMap[article.id] * ratio * 100) / 100;
            });
          }

          // Helper to get display price for an article
          const getArticlePrice = (article) => articlePriceMap[article.id] || 0;

          return (
        <Section>
          <SectionTitle>Artículos del Pedido</SectionTitle>

          {/* Mobile view - Cards */}
          <MobileArticlesList>
            {computedArticles.length > 0 ? (
              computedArticles.map((article) => (
                <MobileArticleCard key={article.id}>
                  <MobileArticleDescription>
                    {article.description}
                    {article.hasDiscount && (
                      <DiscountBadge $isDiscount={article.discountPercentage > 0}>
                        {article.discountPercentage > 0
                          ? `Descuento: -${article.discountPercentage}%`
                          : `Aumento: +${Math.abs(article.discountPercentage)}%`
                        }
                      </DiscountBadge>
                    )}
                    {article.notes && (
                      <div style={{ fontSize: '0.9rem', color: '#666', fontStyle: 'italic', marginTop: '0.5rem' }}>
                        📝 {article.notes}
                      </div>
                    )}
                  </MobileArticleDescription>
                  <MobileArticleDetails>
                    <MobileArticleInfo>
                      {article.dimensions} · Cant: {article.quantity}
                    </MobileArticleInfo>
                    <MobileArticleTotal>
                      {formatCurrency(getArticlePrice(article))}
                    </MobileArticleTotal>
                  </MobileArticleDetails>
                  <MobileArticleActions>
                    <MobileEditButton
                      onClick={() => canEdit ? handleEditArticle(article) : toast.warning(editBlockedReason)}
                      style={{
                        opacity: canEdit ? 1 : 0.5,
                        cursor: canEdit ? 'pointer' : 'not-allowed'
                      }}
                      title={canEdit ? 'Editar artículo' : editBlockedReason}
                    >
                      Editar
                    </MobileEditButton>
                  </MobileArticleActions>
                </MobileArticleCard>
              ))
            ) : (
              <div style={{ textAlign: 'center', color: '#666', padding: '1rem' }}>
                No hay artículos en este pedido
              </div>
            )}
          </MobileArticlesList>

          {/* Desktop view - Table */}
          <ItemsTable>
            <thead>
              <tr>
                <TableHeader>Descripción</TableHeader>
                <TableHeader>Dimensiones</TableHeader>
                <TableHeader>Cantidad</TableHeader>
                <TableHeader>Total</TableHeader>
                <TableHeader>Acciones</TableHeader>
              </tr>
            </thead>
            <tbody>
              {computedArticles.length > 0 ? (
                computedArticles.map((article) => (
                  <tr key={article.id}>
                    <TableCell style={{ maxWidth: '300px', wordBreak: 'break-word' }}>
                      {article.description}
                      {article.hasDiscount && (
                        <div style={{
                          fontSize: '0.85rem',
                          color: article.discountPercentage > 0 ? '#d9534f' : '#5cb85c',
                          marginTop: '0.25rem',
                          fontWeight: '500'
                        }}>
                          {article.discountPercentage > 0
                            ? `Descuento aplicado: -${article.discountPercentage}%`
                            : `Aumento aplicado: +${Math.abs(article.discountPercentage)}%`
                          }
                        </div>
                      )}
                      {article.notes && (
                        <div style={{ fontSize: 'inherit', color: '#666', fontStyle: 'italic', marginTop: '0.25rem' }}>
                          📝 {article.notes}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>{article.dimensions}</TableCell>
                    <TableCell>{article.quantity}</TableCell>

                    <TableCell><strong>{formatCurrency(getArticlePrice(article))}</strong></TableCell>
                    <TableCell>
                      <button
                        style={{
                          padding: '0.25rem 0.5rem',
                          backgroundColor: canEdit ? '#007bff' : '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: canEdit ? 'pointer' : 'not-allowed',
                          fontSize: '0.75rem',
                          opacity: canEdit ? 1 : 0.5
                        }}
                        onClick={() => canEdit ? handleEditArticle(article) : toast.warning(editBlockedReason)}
                        title={canEdit ? 'Editar artículo' : editBlockedReason}
                      >
                        Editar
                      </button>
                    </TableCell>
                  </tr>
                ))
              ) : (
                <tr>
                  <TableCell colSpan="7" style={{ textAlign: 'center', color: '#666' }}>
                    No hay artículos en este pedido
                  </TableCell>
                </tr>
              )}
            </tbody>
          </ItemsTable>
        </Section>
          );
        })()}

        <TotalSection>
          {(() => {
            const isPro = isCalculadoraPro(order);
            if (isPro) {
              // PRO: el subtotal = suma de las bases POR ARTÍCULO redondeadas a 5 cts, igual que se
              // muestran las líneas y que el total guardado. Antes se sumaban las bases en crudo, y con
              // un precio manual que no fuera múltiplo de 5 cts el subtotal no cuadraba con la línea
              // (p.ej. manual 98,91 → la línea mostraba 98,95 pero el subtotal 98,91).
              const proArticleBases = {};
              (order.lines || []).forEach(line => {
                const fp = line.metadata?.fingerprint || `line-${line.id}`;
                proArticleBases[fp] = (proArticleBases[fp] || 0) + parseFloat(line.totalPrice || 0);
              });
              const linesTotalBase = Object.values(proArticleBases).reduce((sum, base) => sum + roundToFiveCents(base), 0);
              const discountAmount = linesTotalBase * (parseFloat(order.discountPercentage) || 0) / 100;
              return (
                <>
                  <TotalRow>
                    <span>Subtotal:</span>
                    <span>{formatCurrency(linesTotalBase)}</span>
                  </TotalRow>

                  {order.discountPercentage > 0 && (
                    <TotalRow>
                      <span>Descuento ({order.discountPercentage}%):</span>
                      <span>-{formatCurrency(discountAmount)}</span>
                    </TotalRow>
                  )}

                  <TotalRow isTotal>
                    <span>Total (IVA incluido):</span>
                    <span>{formatCurrency(calculateDisplayFinalAmount(order))}</span>
                  </TotalRow>
                </>
              );
            } else {
              // TPV: finalAmount = lo que PAGA el cliente (descuento ya aplicado, IVA incluido) —
              // semántica unificada POST-dto (jul-2026). El subtotal pre-descuento se deriva
              // dividiendo; NO volver a restar el descuento aquí (doblaba el dto, bug #2132).
              const tpvTotal = calculateDisplayFinalAmount(order);
              const discPct = parseFloat(order.discountPercentage) || 0;
              const hasDisc = discPct > 0 && discPct < 100;
              const tpvSubtotal = hasDisc ? tpvTotal / (1 - discPct / 100) : tpvTotal;
              const discAmount = tpvSubtotal - tpvTotal;
              return (
                <>
                  <TotalRow>
                    <span>Subtotal (IVA incluido):</span>
                    <span>{formatCurrency(tpvSubtotal)}</span>
                  </TotalRow>

                  {hasDisc && (
                    <TotalRow>
                      <span>Descuento ({discPct}%):</span>
                      <span>-{formatCurrency(discAmount)}</span>
                    </TotalRow>
                  )}

                  <TotalRow isTotal>
                    <span>Total (IVA incluido):</span>
                    <span>{formatCurrency(tpvTotal)}</span>
                  </TotalRow>
                </>
              );
            }
          })()}
        </TotalSection>

        <ButtonGroup>
          <Button variant="primary" onClick={handlePrint}>
            {isCalculadoraPro(order) ? 'Imprimir Pedido' : 'Imprimir Presupuesto'}
          </Button>
          <Button variant="secondary" onClick={handlePrintPresupuestoNotas}>
            Presupuesto con notas
          </Button>
          {/* Botones específicos para TPV */}
          {!isCalculadoraPro(order) && (
            <>
              <Button variant="secondary" onClick={handlePrintTicket}>
                Imprimir Ticket
              </Button>
              <Button
                variant="success"
                onClick={handleGenerateInvoice}
                disabled={isGeneratingInvoice}
              >
                {isGeneratingInvoice ? 'Generando...' : 'Generar Factura'}
              </Button>
            </>
          )}
          {order.status === 'confirmed' && (
            <Button variant="primary" onClick={handleSyncOdoo} disabled={isSyncingOdoo}>
              {isSyncingOdoo ? 'Sincronizando...' : 'Sincronizar con Odoo'}
            </Button>
          )}
          {!isDelivered && !isInvoiced && (
            <Button
              variant="secondary"
              onClick={handlePrintWorkOrder}
              disabled={isPrinting}
            >
              {isPrinting ? 'Generando...' : 'Imprimir Orden de Trabajo'}
            </Button>
          )}
          {canGenerateInvoice && isCalculadoraPro(order) && (
            <Button
              variant="success"
              onClick={handleGenerateInvoice}
              disabled={isGeneratingInvoice}
            >
              {isGeneratingInvoice ? 'Generando...' : 'Generar Factura'}
            </Button>
          )}
          {canGenerateDeliveryNote && (
            <Button 
              variant="info" 
              onClick={handleGenerateDeliveryNote}
              disabled={isGeneratingDeliveryNote}
            >
              {isGeneratingDeliveryNote ? 'Generando...' : 'Generar Albarán'}
            </Button>
          )}
        </ButtonGroup>

        {/* Work Order Info Section */}
        {workOrder && (
          <Section style={{ marginTop: '2rem', backgroundColor: '#f0f8ff', padding: '1rem', borderRadius: '4px' }}>
            <SectionTitle>Información de Orden de Trabajo</SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoLabel>Nº Orden:</InfoLabel>
                <InfoValue>OT-{workOrder.workOrderNumber}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Estado:</InfoLabel>
                <InfoValue>{getWorkOrderStatusLabel(workOrder.status)}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Impreso:</InfoLabel>
                <InfoValue>{workOrder.printedAt ? formatDate(workOrder.printedAt) : 'No impreso'}</InfoValue>
              </InfoItem>
              <InfoItem>
                <InfoLabel>Creado:</InfoLabel>
                <InfoValue>{formatDate(workOrder.createdAt)}</InfoValue>
              </InfoItem>
            </InfoGrid>
          </Section>
        )}
      </OrderCard>

      {/* Edit Article Modal */}
      {showEditModal && editingArticle && (
        <ModalOverlay onClick={handleCloseModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Editar Artículo</ModalTitle>
              <CloseButton onClick={handleCloseModal}>×</CloseButton>
            </ModalHeader>

            <EditForm>
              <FormGroup>
                <Label>Dimensiones</Label>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <Input
                    type="number"
                    placeholder="Ancho"
	                    value={editingArticle.width || ''}
	                    onChange={(e) => {
		                      const newWidth = parseFloat(e.target.value) || 0;
		                      const marginMultiplier = editingArticle.marginMultiplier || 1;

                      // Recalculate prices for all lines based on new outer dimensions + per-line deltas
                      const updatedLines = editingArticle.lines.map(line => {
                        if (line.product) {
                          let newTotalPrice = line.totalPrice;
                          const height = editingArticle.height || 0;
                          const lineWidth = newWidth + (line._deltaWidth || 0);
                          const lineHeight = height + (line._deltaHeight || 0);
                          const basePrice = parseFloat(line.product.basePrice) || 0;
                          const effectiveUnit = basePrice * marginMultiplier;
                          
                          if (line.product.unit === 'ml') {
                            // Linear meters (perimeter)
                            const perimeter = ((lineWidth + lineHeight) * 2) / 100;
                            newTotalPrice = perimeter * effectiveUnit * editingArticle.quantity;
                          } else if (line.product.unit === 'm2') {
                            // Square meters (area)
                            const area = (lineWidth * lineHeight) / 10000;
                            newTotalPrice = area * effectiveUnit * editingArticle.quantity;
                          } else {
                            newTotalPrice = effectiveUnit * editingArticle.quantity;
                          }
                          
                          return { ...line, width: lineWidth, height: lineHeight, totalPrice: newTotalPrice };
                        }
                        return line;
                      });

                      const summedTotal = updatedLines.reduce((sum, l) => sum + (l.totalPrice || 0), 0);
                      // Apply 5-cent rounding only for Calculadora Profesional orders
                      const newTotalAmount = isCalculadoraPro(order) ? roundToFiveCents(summedTotal) : summedTotal;

                      setEditingArticle({
                        ...editingArticle,
                        width: newWidth,
                        dimensions: `${newWidth}x${editingArticle.height || ''}`,
                        lines: updatedLines,
                        totalAmount: newTotalAmount,
                        unitPrice: editingArticle.quantity > 0 ? newTotalAmount / editingArticle.quantity : 0
                      });
                    }}
                    style={{ width: '80px' }}
                  />
                  <span>×</span>
                  <Input
                    type="number"
                    placeholder="Alto"
	                    value={editingArticle.height || ''}
	                    onChange={(e) => {
	                      const newHeight = parseFloat(e.target.value) || 0;
                      const marginMultiplier = editingArticle.marginMultiplier || 1;
                      
                      // Recalculate prices for all lines based on new outer dimensions + per-line deltas
                      const updatedLines = editingArticle.lines.map(line => {
                        if (line.product) {
                          let newTotalPrice = line.totalPrice;
                          const width = editingArticle.width || 0;
                          const lineWidth = width + (line._deltaWidth || 0);
                          const lineHeight = newHeight + (line._deltaHeight || 0);
                          const basePrice = parseFloat(line.product.basePrice) || 0;
                          const effectiveUnit = basePrice * marginMultiplier;
                          
                          if (line.product.unit === 'ml') {
                            // Linear meters (perimeter)
                            const perimeter = ((lineWidth + lineHeight) * 2) / 100;
                            newTotalPrice = perimeter * effectiveUnit * editingArticle.quantity;
                          } else if (line.product.unit === 'm2') {
                            // Square meters (area)
                            const area = (lineWidth * lineHeight) / 10000;
                            newTotalPrice = area * effectiveUnit * editingArticle.quantity;
                          } else {
                            newTotalPrice = effectiveUnit * editingArticle.quantity;
                          }
                          
                          return { ...line, width: lineWidth, height: lineHeight, totalPrice: newTotalPrice };
                        }
                        return line;
                      });

                      const summedTotal = updatedLines.reduce((sum, l) => sum + (l.totalPrice || 0), 0);
                      // Apply 5-cent rounding only for Calculadora Profesional orders
                      const newTotalAmount = isCalculadoraPro(order) ? roundToFiveCents(summedTotal) : summedTotal;

                      setEditingArticle({
                        ...editingArticle,
                        height: newHeight,
                        dimensions: `${editingArticle.width || ''}x${newHeight}`,
                        lines: updatedLines,
                        totalAmount: newTotalAmount,
                        unitPrice: editingArticle.quantity > 0 ? newTotalAmount / editingArticle.quantity : 0
                      });
                    }}
                    style={{ width: '80px' }}
                  />
                  <span>cm</span>
                </div>
              </FormGroup>

              <FormGroup>
                <Label>Cantidad</Label>
                <Input
                  type="number"
	                  value={editingArticle.quantity}
	                  onChange={(e) => {
	                    const newQuantity = parseInt(e.target.value) || 1;
	                    const marginMultiplier = editingArticle.marginMultiplier || 1;
                    
	                    // Recalculate prices for all lines based on new quantity
	                    const updatedLines = editingArticle.lines.map(line => {
	                      const width = parseFloat(editingArticle.width) || 0;
	                      const height = parseFloat(editingArticle.height) || 0;
	                      const lineWidth = width + (line._deltaWidth || 0);
	                      const lineHeight = height + (line._deltaHeight || 0);
	                      
	                      if (line.product && line.product.basePrice) {
	                        const basePrice = parseFloat(line.product.basePrice) || 0;
	                        const effectiveUnit = basePrice * marginMultiplier;
	                        let newTotalPrice = 0;
	                        
	                        if (line.product.unit === 'ml') {
	                          // Linear meters (perimeter)
	                          const perimeter = ((lineWidth + lineHeight) * 2) / 100;
	                          newTotalPrice = perimeter * effectiveUnit * newQuantity;
	                        } else if (line.product.unit === 'm2') {
	                          // Square meters (area)
	                          const area = (lineWidth * lineHeight) / 10000;
	                          newTotalPrice = area * effectiveUnit * newQuantity;
	                        } else {
	                          // Unit based
	                          newTotalPrice = effectiveUnit * newQuantity;
	                        }
	                        
	                        return { ...line, width: lineWidth, height: lineHeight, totalPrice: newTotalPrice };
	                      } else {
                        // If no product info, scale proportionally only if we have a valid price
                        const oldQuantity = editingArticle.quantity || 1;
                        const currentPrice = parseFloat(line.totalPrice) || 0;
                        
                        if (currentPrice > 0 && oldQuantity > 0) {
                          const unitPrice = currentPrice / oldQuantity;
                          return { ...line, totalPrice: unitPrice * newQuantity };
                        } else {
                          // Keep the current price if we can't calculate
                          return { ...line, totalPrice: currentPrice };
                        }
                      }
                    });

                    const summedTotal = updatedLines.reduce((sum, l) => sum + (parseFloat(l.totalPrice) || 0), 0);
                    // Apply 5-cent rounding only for Calculadora Profesional orders
                    const newTotalAmount = isCalculadoraPro(order) ? roundToFiveCents(summedTotal) : summedTotal;

                    setEditingArticle({
                      ...editingArticle,
                      quantity: newQuantity,
                      lines: updatedLines,
                      totalAmount: newTotalAmount,
                      unitPrice: newQuantity > 0 ? newTotalAmount / newQuantity : 0
                    });
                  }}
                />
              </FormGroup>


              <FormGroup>
                <Label>Materiales</Label>
                <MaterialsList>
                  {editingArticle.lines && editingArticle.lines.map((line, index) => (
                    <MaterialItem key={line.id} style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                        <strong>{formatLineTypeTitle(line.lineType)}</strong>
                        <span style={{ fontSize: '0.875rem', color: '#666' }}>
                          {formatCurrency(line.totalPrice || 0)}
                        </span>
                      </div>
                      <select
	                        value={line.productId || ''}
	                        onChange={(e) => {
	                          const newProductId = e.target.value;
	                          const productsForType = getProductsForLineType(line.lineType);
	                          const selectedProduct = newProductId ?
	                            productsForType.find(p => p.id === newProductId) : null;
	                          const marginMultiplier = editingArticle.marginMultiplier || 1;

	                          // Update the line with new product and calculate new price
	                          const updatedLines = editingArticle.lines.map((l, i) => {
	                            if (i === index) {
	                              // Calculate new price based on product's base price and dimensions
	                              let newTotalPrice = 0; // Default to 0 if no product selected
	                              
	                              if (selectedProduct && selectedProduct.basePrice) {
	                                const basePrice = parseFloat(selectedProduct.basePrice) || 0;
	                                const effectiveUnit = basePrice * marginMultiplier;
	                                const quantity = editingArticle.quantity || 1;
	                                // Calculate based on lineType (not product.unit which is 'Unit(s)' in Odoo)
	                                const outerW = editingArticle.width || 0;
	                                const outerH = editingArticle.height || 0;
	                                const width = outerW + (l._deltaWidth || 0);
	                                const height = outerH + (l._deltaHeight || 0);

	                                // Use lineType to determine calculation method
	                                const isMoldura = l.lineType === 'moldura';
	                                const isAccessory = l.lineType && l.lineType.includes('accesorio');

	                                if (isMoldura) {
	                                  // Linear meters (perimeter) for molduras (minimum 20x20 for pricing)
	                                  const pW = Math.max(parseFloat(width), 20);
	                                  const pH = Math.max(parseFloat(height), 20);
	                                  const perimeter = ((pW + pH) * 2) / 100;
	                                  newTotalPrice = perimeter * effectiveUnit * quantity;
	                                } else if (isAccessory) {
	                                  // Unit based for accessories
	                                  newTotalPrice = effectiveUnit * quantity;
	                                } else {
	                                  // Square meters (area) for passepartout, cristal, trasera (minimum 20x20)
	                                  const pW = Math.max(parseFloat(width), 20);
	                                  const pH = Math.max(parseFloat(height), 20);
	                                  const area = (pW * pH) / 10000;
	                                  newTotalPrice = area * effectiveUnit * quantity;
	                                }
	                              }
                              
                              return { 
                                ...l, 
                                productId: newProductId || null,
	                                product: selectedProduct ? {
	                                  ...selectedProduct,
	                                  basePrice: parseFloat(selectedProduct.basePrice) || 0
	                                } : null,
	                                width: (editingArticle.width || 0) + (l._deltaWidth || 0),
	                                height: (editingArticle.height || 0) + (l._deltaHeight || 0),
	                                totalPrice: newTotalPrice
	                              };
                            }
                            return l;
                          });

                          // Recalculate total amount for the article
                          const summedTotal = updatedLines.reduce((sum, l) => sum + (parseFloat(l.totalPrice) || 0), 0);
                          // Apply 5-cent rounding only for Calculadora Profesional orders
                          const newTotalAmount = isCalculadoraPro(order) ? roundToFiveCents(summedTotal) : summedTotal;

                          setEditingArticle({
                            ...editingArticle,
                            lines: updatedLines,
                            totalAmount: newTotalAmount,
                            unitPrice: editingArticle.quantity > 0 ? newTotalAmount / editingArticle.quantity : 0
                          });
                        }}
                        style={{
                          width: '100%',
                          padding: '0.5rem',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '0.875rem'
                        }}
                      >
                        <option value="">Seleccionar {formatLineTypeTitle(line.lineType)}...</option>
                        {getProductsForLineType(line.lineType).map(product => (
                          <option key={product.id} value={product.id}>
                            {product.name} - €{product.basePrice}/{product.unit}
                          </option>
                        ))}
                      </select>
                      {line.lineType === 'passepartout' && (
                        <div style={{ marginTop: '0.5rem' }}>
                          <Label style={{ fontSize: '0.75rem' }}>Ancho del passepartout (cm)</Label>
                          <Input
                            type="number"
                            value={line.width || 5}
                            onChange={(e) => {
                              const newWidth = parseFloat(e.target.value) || 5;
                              const updatedLines = editingArticle.lines.map((l, i) => 
                                i === index ? { ...l, width: newWidth } : l
                              );
                              setEditingArticle({
                                ...editingArticle,
                                lines: updatedLines
                              });
                            }}
                            style={{ width: '80px', fontSize: '0.875rem' }}
                          />
                        </div>
                      )}
                    </MaterialItem>
                  ))}
                </MaterialsList>

                {/* Add new component section */}
                {!showAddComponentUI ? (
                  <button
                    type="button"
                    onClick={() => setShowAddComponentUI(true)}
                    style={{
                      marginTop: '1rem',
                      padding: '0.5rem 1rem',
                      backgroundColor: '#4caf50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      fontSize: '0.875rem'
                    }}
                  >
                    + Añadir componente
                  </button>
                ) : (
                  <div style={{
                    marginTop: '1rem',
                    padding: '1rem',
                    backgroundColor: '#e8f5e9',
                    border: '1px dashed #4caf50',
                    borderRadius: '4px'
                  }}>
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem', alignItems: 'center' }}>
                      <select
                        value={newComponentType}
                        onChange={(e) => {
                          setNewComponentType(e.target.value);
                          setNewComponentProductId('');
                        }}
                        style={{ flex: 1, padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                      >
                        {lineTypeOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                      <select
                        value={newComponentProductId}
                        onChange={(e) => setNewComponentProductId(e.target.value)}
                        style={{ flex: 2, padding: '0.5rem', borderRadius: '4px', border: '1px solid #ddd' }}
                      >
                        <option value="">
                          {getProductsForLineType(newComponentType).length === 0
                            ? 'No hay productos'
                            : 'Seleccionar producto...'}
                        </option>
                        {getProductsForLineType(newComponentType).map(product => (
                          <option key={product.id} value={product.id}>
                            {product.name} - {formatCurrency(parseFloat(product.basePrice) || 0)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button
                        type="button"
                        onClick={handleAddComponentToArticle}
                        disabled={!newComponentProductId}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: newComponentProductId ? '#4caf50' : '#ccc',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: newComponentProductId ? 'pointer' : 'not-allowed',
                          fontSize: '0.875rem'
                        }}
                      >
                        Añadir
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowAddComponentUI(false);
                          setNewComponentProductId('');
                        }}
                        style={{
                          padding: '0.5rem 1rem',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.875rem'
                        }}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label>Total</Label>
                <Input
                  type="text"
                  value={formatCurrency(editingArticle.totalAmount)}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </FormGroup>
            </EditForm>

            <ModalActions>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={() => handleSaveArticle(editingArticle)}>
                Guardar Cambios
              </Button>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

      {order?.metadata?.odooSync && (
        <Section style={{ marginTop: '1.5rem' }}>
          <SectionTitle>Odoo</SectionTitle>
          <InfoGrid>
            <InfoItem>
              <InfoLabel>Estado Sync:</InfoLabel>
              <InfoValue>{order.metadata.odooSync.success ? 'OK' : 'Error'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Modelo:</InfoLabel>
              <InfoValue>{order.metadata.odooSync.model || '-'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>ID Odoo:</InfoLabel>
              <InfoValue>{order.metadata.odooSync.id || '-'}</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>DB:</InfoLabel>
              <InfoValue>{order.metadata.odooSync.db || '-'}</InfoValue>
            </InfoItem>
          </InfoGrid>
        </Section>
      )}

      {/* Calculator Edit Modal - uses full CalculatorPro for editing */}
      {showCalculatorModal && editingArticle && (
        <CalculatorEditModal
          article={editingArticle}
          order={order}
          onSave={handleCalculatorEditSave}
          onClose={() => {
            setShowCalculatorModal(false);
            setEditingArticle(null);
          }}
        />
      )}

      {/* Modal de selección de cliente para facturación */}
      {showClientSelectModal && (
        <ModalOverlay onClick={() => setShowClientSelectModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <ModalHeader>
              <ModalTitle>Seleccionar Cliente para Facturar</ModalTitle>
            </ModalHeader>
            <p style={{ marginBottom: '1rem', color: '#666' }}>
              Para generar una factura es necesario asignar un cliente al pedido.
            </p>
            <ClientSelectWithCreate
              value={selectedClientId || order.client?.id}
              onChange={(clientId) => setSelectedClientId(clientId)}
              placeholder="Buscar o crear cliente..."
              fetchAllClients={true}
            />
            <ModalActions>
              <button
                onClick={() => {
                  setShowClientSelectModal(false);
                  setSelectedClientId(null);
                }}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={async () => {
                  if (!selectedClientId) {
                    toast.warning('Debe seleccionar un cliente');
                    return;
                  }
                  try {
                    // Actualizar el cliente del pedido
                    await api.patch(`/orders/${id}`, { clientId: selectedClientId });
                    queryClient.invalidateQueries(['order', id]);
                    queryClient.invalidateQueries(['orders']);
                    setShowClientSelectModal(false);
                    toast.success('Cliente asignado. Ahora puede generar la factura.');
                  } catch (error) {
                    console.error('Error updating client:', error);
                    toast.error('Error al asignar el cliente');
                  }
                }}
                disabled={!selectedClientId}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: selectedClientId ? '#28a745' : '#ccc',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: selectedClientId ? 'pointer' : 'not-allowed'
                }}
              >
                Asignar Cliente
              </button>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}

    </DetailContainer>
  );
};

export default OrderDetail;
